import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { CheckCircle, ChevronRight, Loader2, Database, Zap, Shield, Globe } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Globe },
  { id: 'admin', title: 'Admin Account', icon: Shield },
  { id: 'odoo', title: 'Odoo ERP', icon: Database },
  { id: 'ai', title: 'AI Configuration', icon: Zap },
  { id: 'done', title: 'Ready!', icon: CheckCircle },
];

export default function OnboardingWizard({ onComplete }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState({});

  const [adminForm, setAdminForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [odooForm, setOdooForm] = useState({ name: 'Odoo ERP', url: 'https://erp.example.com', database: '', username: '', api_key: '' });
  const [aiForm, setAiForm] = useState({ llm_api_key: '', company_name: '' });

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));

  const handleAdmin = async () => {
    if (adminForm.password !== adminForm.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/onboarding/step/admin`, {
        name: adminForm.name, email: adminForm.email, password: adminForm.password
      });
      setCompleted(c => ({ ...c, admin: true }));
      toast.success('Admin account created!');
      next();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create admin');
    } finally { setLoading(false); }
  };

  const handleOdoo = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/onboarding/step/odoo`, odooForm);
      setCompleted(c => ({ ...c, odoo: true }));
      toast.success(`Connected to Odoo as UID ${res.data.uid}`);
      next();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Odoo connection failed');
    } finally { setLoading(false); }
  };

  const handleAI = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/onboarding/step/ai`, { llm_api_key: aiForm.llm_api_key });
      await axios.post(`${API_URL}/api/onboarding/complete`, { company_name: aiForm.company_name });
      setCompleted(c => ({ ...c, ai: true, done: true }));
      toast.success('Setup complete!');
      next();
    } catch (e) {
      toast.error('Setup failed. You can configure this later in Settings.');
    } finally { setLoading(false); }
  };

  const StepIcon = STEPS[step].icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#800000]/10 to-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#800000] rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
            <span className="text-white text-2xl font-bold">ERP</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Securado ERP AI</h1>
          <p className="text-gray-500 text-sm mt-1">First-time setup wizard</p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center mb-8 gap-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step || completed[s.id];
            return (
              <React.Fragment key={s.id}>
                <div className={`flex flex-col items-center gap-1 ${isActive ? 'opacity-100' : isDone ? 'opacity-70' : 'opacity-30'}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${isActive ? 'bg-[#800000] border-[#800000] text-white' : isDone ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-gray-300 text-gray-400'}`}>
                    {isDone && i < step ? <CheckCircle className="h-5 w-5" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span className="text-[10px] text-gray-500 hidden sm:block">{s.title}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`h-0.5 w-8 sm:w-12 ${i < step ? 'bg-emerald-500' : 'bg-gray-200'}`} />}
              </React.Fragment>
            );
          })}
        </div>

        <Card className="shadow-xl border-0">
          <CardContent className="p-8">
            {/* Step 0: Welcome */}
            {step === 0 && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-[#800000]/10 rounded-full flex items-center justify-center mx-auto">
                  <Globe className="h-8 w-8 text-[#800000]" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Welcome to Securado ERP AI</h2>
                <p className="text-gray-500 text-sm leading-relaxed max-w-md mx-auto">
                  This wizard will guide you through setting up your ERP AI platform.
                  You'll need: an admin account, your Odoo ERP credentials, and optionally an AI API key.
                </p>
                <div className="grid grid-cols-3 gap-3 pt-2">
                  {[
                    { icon: Shield, label: 'Admin Account', color: 'text-blue-500' },
                    { icon: Database, label: 'Odoo Connection', color: 'text-purple-500' },
                    { icon: Zap, label: 'AI Features', color: 'text-amber-500' },
                  ].map(({ icon: Icon, label, color }) => (
                    <div key={label} className="text-center p-3 rounded-lg bg-gray-50">
                      <Icon className={`h-6 w-6 mx-auto mb-1 ${color}`} />
                      <p className="text-xs text-gray-600 font-medium">{label}</p>
                    </div>
                  ))}
                </div>
                <Button onClick={next} className="w-full bg-[#800000] hover:bg-[#9a1919] text-white mt-4">
                  Get Started <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}

            {/* Step 1: Admin Account */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[#800000]" /> Create Admin Account
                </h2>
                <p className="text-sm text-gray-500">This will be the superadmin account for your ERP AI.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Full Name</Label>
                    <Input value={adminForm.name} onChange={e => setAdminForm(f => ({...f, name: e.target.value}))} placeholder="Your name" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={adminForm.email} onChange={e => setAdminForm(f => ({...f, email: e.target.value}))} placeholder="admin@company.com" className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Password (min 8 chars)</Label>
                    <Input type="password" value={adminForm.password} onChange={e => setAdminForm(f => ({...f, password: e.target.value}))} placeholder="••••••••" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Confirm Password</Label>
                    <Input type="password" value={adminForm.confirm} onChange={e => setAdminForm(f => ({...f, confirm: e.target.value}))} placeholder="••••••••" className="mt-1" />
                  </div>
                </div>
                <Button onClick={handleAdmin} disabled={loading || !adminForm.email || !adminForm.password} className="w-full bg-[#800000] hover:bg-[#9a1919] text-white">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create Admin Account <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}

            {/* Step 2: Odoo */}
            {step === 2 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Database className="h-5 w-5 text-[#800000]" /> Connect Odoo ERP
                </h2>
                <p className="text-sm text-gray-500">Connect to your Odoo instance to sync CRM data.</p>
                <div>
                  <Label className="text-xs">Connection Name</Label>
                  <Input value={odooForm.name} onChange={e => setOdooForm(f => ({...f, name: e.target.value}))} className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Odoo URL</Label>
                    <Input value={odooForm.url} onChange={e => setOdooForm(f => ({...f, url: e.target.value}))} placeholder="https://erp.company.com" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Database Name</Label>
                    <Input value={odooForm.database} onChange={e => setOdooForm(f => ({...f, database: e.target.value}))} placeholder="mycompany" className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Username / Email</Label>
                    <Input value={odooForm.username} onChange={e => setOdooForm(f => ({...f, username: e.target.value}))} placeholder="admin@company.com" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">API Key</Label>
                    <Input type="password" value={odooForm.api_key} onChange={e => setOdooForm(f => ({...f, api_key: e.target.value}))} placeholder="Odoo API Key" className="mt-1" />
                    <p className="text-[10px] text-gray-400 mt-0.5">Settings → Technical → API Keys in Odoo</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={next} className="flex-1">Skip for now</Button>
                  <Button onClick={handleOdoo} disabled={loading || !odooForm.url || !odooForm.database} className="flex-1 bg-[#800000] hover:bg-[#9a1919] text-white">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Connect & Test <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: AI */}
            {step === 3 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-amber-500" /> AI Configuration
                </h2>
                <p className="text-sm text-gray-500">Configure AI features. You can skip this and set up later in Settings.</p>
                <div>
                  <Label className="text-xs">Company Name</Label>
                  <Input value={aiForm.company_name} onChange={e => setAiForm(f => ({...f, company_name: e.target.value}))} placeholder="Securado" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    LLM API Key
                    <Badge variant="outline" className="text-[9px]">Optional</Badge>
                  </Label>
                  <Input type="password" value={aiForm.llm_api_key} onChange={e => setAiForm(f => ({...f, llm_api_key: e.target.value}))}
                    placeholder="Emergent Universal Key or OpenAI key" className="mt-1" />
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Enables AI assistant, deal insights, and activity recommendations.
                    Get your Emergent Universal Key at app.emergentlabs.ai → Profile → Universal Key
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setAiForm(f => ({...f, llm_api_key: ''})); handleAI(); }} className="flex-1" disabled={loading}>
                    Skip AI for now
                  </Button>
                  <Button onClick={handleAI} disabled={loading} className="flex-1 bg-[#800000] hover:bg-[#9a1919] text-white">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Complete Setup <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Done */}
            {step === 4 && (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-10 w-10 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">All Set!</h2>
                <p className="text-gray-500 text-sm">Your Securado ERP AI platform is ready. Let's get started!</p>
                <div className="grid grid-cols-3 gap-3 pt-2 text-left">
                  {[
                    { label: 'Admin Created', done: completed.admin },
                    { label: 'Odoo Connected', done: completed.odoo },
                    { label: 'AI Configured', done: completed.ai },
                  ].map(item => (
                    <div key={item.label} className={`p-3 rounded-lg border ${item.done ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                      {item.done ? <CheckCircle className="h-4 w-4 text-emerald-500 mb-1" /> : <div className="h-4 w-4 rounded-full border-2 border-gray-300 mb-1" />}
                      <p className="text-xs font-medium text-gray-700">{item.label}</p>
                    </div>
                  ))}
                </div>
                <Button onClick={onComplete} className="w-full bg-[#800000] hover:bg-[#9a1919] text-white text-base h-11 mt-2">
                  Go to Dashboard <ChevronRight className="h-5 w-5 ml-1" />
                </Button>
                <p className="text-[10px] text-gray-400">
                  You can change all settings later under Admin → Settings
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
